import PageWithNavbar from "@/components/PageWithNavbar";
const AlgorithmServerPage = () => {
  return (
    <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto text-left mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mx-auto text-left">
            Algo Server <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">Guide</span>
        </h1>
        <p className="text-xl text-muted-foreground mt-2 mb-8" >
        A cross-platform, headless algorithmic trading server for self-hosting your trading strategies.
        </p>
        <h3 className="text-lg font-semibold">Getting Started</h3>
        <p className="text-muted-foreground mb-4">
            You've tested your algorithm, verified it's working as expected, but you don't want the overhead of the full Doyen application. You're in luck! We provide a suite of applications that allow you to self-host Doyen algorithms. Whether that's your personal computer, a dedicated server, or a cloud instance.
        </p>
        <p className="text-muted-foreground mb-4">
            This guide assumes you know the basics of running command line applications, have Python installed (or equivalent if you're running a non-default ScriptManager), and have at least a basic understanding of how Doyen works. If you haven't already, please refer to the <a href="/setup" target="_blank" className="text-primary">Setup Guide</a> for instructions on installing Doyen and setting up your first algorithm.
        </p>
        <p className="text-muted-foreground mb-4">As with anything related to Doyen: if you need help or wish to share your feedback <a href="/support" target="_blank" className="text-primary">don't hesitate to reach out by phone or email.</a></p>
        <p className="text-muted-foreground mb-4">
            There are four parts to running your algorithm:
            <ol className="list-decimal list-inside mb-4">
                <li><span className="text-white">Venues</span> which connect to various cryptocurrency exchanges and send data to the Drasil Server.</li>
                <li><span className="text-white">Clusters</span> which run your algorithms / strategies.</li>
                <li><span className="text-white">Server</span> which aggregates data from Venues and communicates with the Algorithm Server.</li>
                <li><span className="text-white">Commanad Line Interface (CLI)</span> which is used for communicating with the Server to start, stop, and configure algorithms on your Clusters.</li>
            </ol>
            When you're starting the applications up, they must be launched in this order. That may sound complicated, but each is its own application for good reason, and once set up the environment provides flexibility, resiliency, and scalability.
        </p>
        <p className="text-muted-foreground mb-2">For reference, here's a diagram of how the components interact:</p>
        <img src="/algo-server-flow.png" alt="Algorithm Server Diagram" className="mb-4"/>
        <p className="text-muted-foreground mb-8">Ready to set it up? Well, let's get started!</p>
        <h3 className="text-lg font-semibold">Venues</h3>
        <p className="text-muted-foreground mb-4">
            Each Drasil Venue is an application that connects to a single cryptocurrency exchange. Its role is streaming market data and order information to the Drasil Server. 
            You can have any number of Venues running, each connected to a different exchange or account.
            You will need to set up a Venue for each exchange you wish to receive data from or trade on.
        </p>
        <img src="/venue-startup.png" alt="Drasil Venue Screenshot" className="mb-4"/>
        <p className="text-muted-foreground mb-2">
            Once started, the Venue will connect to the exchange and begin streaming data to the Drasil Server. Above is an example of successfully starting a Venue for Binance. 
        </p>
        <p className="text-muted-foreground mb-2">
            There are three configuration files for a Drasil Venue. You can see the folder structure below.
            <ol className="list-disc list-inside">
                <li><span className="text-white">exchange-config.json</span> - sets API keys for order management and defines the exchange, base asset (for valuation) and a descriptor name.</li>
                <li><span className="text-white">socket-config.json</span> - address information for the Venue to connect to the Drasil Server.</li>
                <li><span className="text-white">doyen-config.json</span> - your Doyen login credentials</li>
            </ol>
        </p>
        <p className="text-muted-foreground mb-4">For a full list of supported exchanges and their corresponding IDs, please refer to the <a href="https://1130-lab.github.io/Doyen.gRPC/Doyen.gRPC/sabledocs_output/common.html#enum-common.DoyenExchange" target="_blank" className="text-primary">DoyenExchange documentation</a>. Not all exchanges listed are supported at this time.</p>
        <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-4">
            <code className="text-sm">
{`Drasil/
    ...
    DrasilVenue/
        DrasilVenue[.exe|.so]
        Config/
            doyen-config.json
            exchange-config.json
            socket-config.json`}
            </code>
        </pre>
        <code className="text-muted-foreground">Drasil/DrasilVenue/Config/doyen-config.json</code>
        <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-8">
          <code className="text-sm">
{`{
  "DoyenUsername": "<YOUR-USERNAME>",
  "DoyenPassword": "<YOUR-PASSWORD>"
}`}
          </code>
        </pre>
        <code className="text-muted-foreground">Drasil/DrasilVenue/Config/exchange-config.json</code>
        <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-4">
            <code className="text-sm">
{`{
    "Name": "binance.us - Main",
    "Exchange": 1,
    "BaseAsset": "USDT",
    "PublicAPIKey": "<PUBLIC-KEY>",
    "PrivateAPIKey": "<PRIVATE-KEY>"
}`}
            </code>
        </pre>
        <code className="text-muted-foreground">Drasil/DrasilVenue/Config/socket-config.json</code>
        <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-8">
            <code className="text-sm">
{`{
  "DataPublisherAddress": "127.0.0.1",
  "DataPublisherPort": 21001,
  "DataRouterAddress": "127.0.0.1",
  "DataRouterPort": 21002,
  "OrderPublisherAddress": "127.0.0.1",
  "OrderPublisherPort": 21101,
  "OrderRouterAddress": "127.0.0.1",
  "OrderRouterPort": 21102
}`}
            </code>    
        </pre>
        <h3 className="text-lg font-semibold">Clusters</h3>
        <p className="text-muted-foreground mb-4">
            A Cluster is simply the Algorithm ScriptManager running your algorithms. Doyen runs the Python script manager <i>Doyen.Algorithms.ScriptManager.py</i> located at AppData/Local/Doyen/Scripts/Algorithms. If you created and tested your algorithm in Doyen, you can simply run the Doyen.Algorithms.ScriptManager.py and connect your server to 5050/5051.
        </p>
        <img src="/cluster-default-path.png" alt="Drasil Cluster Screenshot" className="mb-4"/>
        <p className="text-muted-foreground mb-4">
            Alternatively, you can run the script with --client [ip]:[port] and --server [ip]:[port] arguments to specify the address of your Drasil Server. Here is an example of starting a Cluster at 5000/6000.
        </p>
        <img src="/cluster-start.png" alt="Drasil Cluster Startup Screenshot" className="mb-4"/>
        <p className="text-muted-foreground mb-4">
            The server address is where data and order information is received from the Drasil Server, while the client address is where the Cluster pushes data (such as an open order request, start confirmation, and so on) to the Drasil Server.
        </p>
        <p className="text-muted-foreground mb-4">By default, we include the Doyen example algorithms in a Drasil download. You can find them in the Drasil/DrasilCluster subfolder.</p>
                <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-4">
            <code className="text-sm">
{`Drasil/
    ...
    DrasilCluster/
        Python/
            Doyen.Algorithms.ScriptManager.py
            Algorithm.py
            --- Default Algorithms ---
            GridTrader.py
            Scalpbot.py
            --- gRPC Files ---
            algos_pb2.py
            algos_pb2_grpc.py
            common_pb2.py
            common_pb2_grpc.py
            `}
            </code>
            </pre>
      <h3 className="text-lg font-semibold">Server</h3>
          <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-4">
      <code className="text-sm">
{`Drasil/
    ...
    DrasilServer/
        DrasilServer[.exe|.so]
        Config/
          doyen-config.json
          server-config.json`}
      </code>
    </pre>
      <p className="text-muted-foreground mb-4">Now the easier parts. The Server requires some basic setup, then you'll be live in no time.</p>
      <p className="text-muted-foreground mb-4">The Server requires you specify two things: Venues and Clusters. Since we've set those up, these settings should look familiar. 
        The Name field of a Venue does not need to match the name you gave it in your exchange-config. 
        Likewise, we are now using the plain names of the Exchange. You can still reference the gRPC documentation if needed.</p>
      <p className="text-muted-foreground mb-4">One field may look unfamiliar to you, which is IgnoreList. 
        This field allows you to specify a list of algorithms (.py files or classes that inherit from the Algorithm class) that the Server should ignore on the associated Cluster, meaning they won't be displayed by or available from the Server.</p>

        <code className="text-muted-foreground">Drasil/DrasilServer/Config/doyen-config.json</code>
        <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-8">
            <code className="text-sm">
{`{
  "DoyenUsername": "<YOUR-USERNAME>",
  "DoyenPassword": "<YOUR-PASSWORD>"
}`}
            </code>
        </pre>

        <code className="text-muted-foreground">Drasil/DrasilServer/Config/server-config.json</code>
        <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-4">
          <code className="text-sm">
{`{
  "Venues": [
    {
      "Name": "Binance-Venue",
      "Exchange": "BinanceUS",
      "DataPublisherAddress": "127.0.0.1",
      "DataPublisherPort": 21001,
      "DataRouterAddress": "127.0.0.1",
      "DataRouterPort": 21002,
      "OrderPublisherAddress": "127.0.0.1",
      "OrderPublisherPort": 21101,
      "OrderRouterAddress": "127.0.0.1",
      "OrderRouterPort": 21102
    }
  ],
  "Clusters": [
    {
      "Name": "LocalCluster",
      "ClusterAddress": "http://localhost:5000",
      "ServerAddress": "http://localhost:6000",
      "IgnoreList": []
    }
  ],
  "LogFilePath": "logs/drasil-server-.log"
}`}
          </code>
        </pre>
        <p className="text-muted-foreground mb-4">With that done, you can start the Drasil Server application. Here's an example of a successfully started server.</p>
        <img src="/server-cli-start.png" alt="Drasil Server Screenshot" className="mb-4"/>
        <p className="text-muted-foreground mb-4">You should see it report back as many algorithms as exist on your Cluster, likewise ensure the Venue you connect to is reports back 'connected successfully.'</p>
        <p className="text-muted-foreground mb-8">The Server is now running and connected to your Venues and Clusters. Next up: the Command Line Interface!</p>
      <h3 className="text-lg font-semibold">Command Line Interface (CLI)</h3>
      <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-4">
      <code className="text-sm">
{`Drasil/
    ...
    DrasilCLI/
        DrasilCLI[.exe|.so]
        client-config.json
        Commands.txt`}
      </code>
    </pre>
      <p className="text-muted-foreground mb-4">The CLI is used to communicate with the Drasil Server and its entire instruction set can be found below. There is a configuration file client-config.json that specifies the Server address.</p>
      <pre className="bg-muted/10 rounded p-4 overflow-x-auto mb-4">
            <code className="text-sm">
{`{
  "Servers": [
    {
      "Name": "main",
      "Address": "http://localhost:6000",
      "IsDefault": true
    }
  ],
  "DefaultServer": "main",
  "OutputFormat": "table"
}`}
    </code>
    </pre>
      <p className="text-muted-foreground mb-4">If you've left everything to default, you can start it up and see the CLI in action. I also ran the algo list command to display the available algorithms on the Server.</p>
      <img src="/client-cli-start.png" alt="Drasil CLI Screenshot" className="mb-4"/>
      <p className="text-muted-foreground mb-4">From here, you can init, start, stop and edit the config files of your algorithms.</p>
      <p className="text-muted-foreground mb-4">That's the entire setup! Congratulations, you now have a full algorithmic, self-hosted, high-performance trading system setup.</p>
    </div>
    </div>
    </div>
    </PageWithNavbar>
  );
};

export default AlgorithmServerPage;
